import { storage } from './storage';
import { STORAGE_KEYS } from '../config';
import { User, AuthResponse } from '../types/auth';

/**
 * Authentication storage utilities
 * Handles storing and retrieving auth data using the unified storage abstraction
 */

/**
 * Store complete authentication data from login/register response
 */
export const storeAuthData = (authResponse: AuthResponse): void => {
  try {
    storage.setItem(STORAGE_KEYS.AUTH_TOKEN, authResponse.token);
    storage.setItem(STORAGE_KEYS.REFRESH_TOKEN, authResponse.refreshToken);
    storage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, authResponse.expiresAt);
    storage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(authResponse.user));
  } catch (error) {
    console.error('Failed to store auth data:', error);
    throw new Error('Failed to store authentication data');
  }
};

/**
 * Get stored user object
 * Returns null if not found or invalid
 */
export const getStoredUser = (): User | null => {
  try {
    const userJson = storage.getItem(STORAGE_KEYS.AUTH_USER);
    if (!userJson) return null;
    return JSON.parse(userJson) as User;
  } catch (error) {
    console.error('Failed to parse stored user:', error);
    return null;
  }
};

/**
 * Get stored access token
 */
export const getStoredToken = (): string | null => {
  return storage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Get stored refresh token
 */
export const getStoredRefreshToken = (): string | null => {
  return storage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * Get token expiry timestamp
 */
export const getTokenExpiry = (): string | null => {
  return storage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
};

/**
 * Check if the stored token is expired
 * Returns true if expired or no token found
 */
export const isTokenExpired = (): boolean => {
  try {
    const expiry = getTokenExpiry();
    if (!expiry) return true;

    const expiryDate = new Date(expiry);
    const now = new Date();

    return now >= expiryDate;
  } catch (error) {
    console.error('Failed to check token expiry:', error);
    return true;
  }
};

/**
 * Check if user is authenticated
 * Validates that token exists and is not expired
 */
export const isAuthenticated = (): boolean => {
  const token = getStoredToken();
  const user = getStoredUser();
  
  if (!token || !user) return false;
  if (isTokenExpired()) return false;

  return true;
};

/**
 * Clear all authentication data
 * Used on logout
 */
export const clearAuthData = (): void => {
  try {
    storage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    storage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
    storage.removeItem(STORAGE_KEYS.AUTH_USER);
  } catch (error) {
    console.error('Failed to clear auth data:', error);
  }
};

/**
 * Update stored user information
 * Useful for profile updates
 */
export const updateStoredUser = (user: User): void => {
  try {
    storage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
  } catch (error) {
    console.error('Failed to update stored user:', error);
    throw new Error('Failed to update user data');
  }
};
